import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeftIcon } from '../icons/ArrowLeftIcon';
import { PlusIcon } from '../icons/PlusIcon';
import { TrashIcon } from '../icons/TrashIcon';
import { PhotoIcon } from '../icons/PhotoIcon';
import { MicrophoneIcon } from '../icons/MicrophoneIcon';
import { UploadIcon } from '../icons/UploadIcon';
import { MagicWandIcon } from '../icons/MagicWandIcon';
import { LoadingSpinner } from '../icons/LoadingSpinner';
import { CharacterPortraitModal } from '../CharacterPortraitModal';
import { UserAssetsPicker } from '../modals/UserAssetsPicker';
import { cloneVoiceWithElevenLabs } from '../../services/elevenlabsService';
import { cropImageToAspectRatio } from '../../services/geminiService';
import { CREDIT_COSTS } from '../../types';

export const CharacterSelection = ({ 
    initialData, 
    onSubmit, 
    onBack, 
    error, 
    onGenerateFullVideo,
    onGenerateImages,
    onDeductCredits,
    onSaveMedia,
    onProjectUpdate,
    onTrackCreditsUsed 
}) => {
    const [characters, setCharacters] = useState(initialData.characters || []);
    const [editingCharacter, setEditingCharacter] = useState(null);
    const [activeTab, setActiveTab] = useState('characters');
    const [cloningStatus, setCloningStatus] = useState({});
    const [viewingImage, setViewingImage] = useState(null); // { url: string, name: string }
    const [uploadMenuOpen, setUploadMenuOpen] = useState(null); // character id when menu is open
    const [assetPickerOpen, setAssetPickerOpen] = useState(false);
    const [assetPickerTargetId, setAssetPickerTargetId] = useState(null);
    const audioFileRefs = useRef({});
    const imageFileRefs = useRef({});

    // Sync local characters state with initialData when it changes from parent
    useEffect(() => {
        if (initialData.characters) {
            console.log('🔄 Syncing characters from initialData:', initialData.characters.length);
            setCharacters(initialData.characters);
        }
    }, [initialData.characters]);

    // Close upload menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setUploadMenuOpen(null);
        if (uploadMenuOpen) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [uploadMenuOpen]);

    const updateCharacters = (newCharacters) => {
        console.log('📝 updateCharacters called with', newCharacters.length, 'characters');
        setCharacters(newCharacters);
        if (onProjectUpdate) {
            console.log('📤 Calling onProjectUpdate');
            onProjectUpdate({ ...initialData, characters: newCharacters });
        }
    };

    const handleCharacterUpdate = (id, field, value) => {
        const newCharacters = characters.map(char => char.id === id ? { ...char, [field]: value } : char);
        updateCharacters(newCharacters);
    };

    // Handle selecting an image from User Assets
    const handleSelectFromAssets = (imageUrl, asset) => {
        if (assetPickerTargetId) {
            console.log('📸 Selected asset for character:', assetPickerTargetId, asset?.name);
            handleCharacterUpdate(assetPickerTargetId, 'image', imageUrl);
            // Also update name if it's empty and asset has a name
            const char = characters.find(c => c.id === assetPickerTargetId);
            if (char && !char.name && asset?.name) {
                handleCharacterUpdate(assetPickerTargetId, 'name', asset.name);
            }
        }
        setAssetPickerOpen(false);
        setAssetPickerTargetId(null);
    };

    // Open upload menu for a character
    const handleUploadClick = (e, charId) => {
        e.stopPropagation();
        setUploadMenuOpen(uploadMenuOpen === charId ? null : charId);
    };

    // Open asset picker for a character
    const openAssetPicker = (charId) => {
        setAssetPickerTargetId(charId);
        setAssetPickerOpen(true);
        setUploadMenuOpen(null);
    };

    const handleAddCharacter = () => {
        const category = activeTab === 'characters' ? 'character' : activeTab === 'elements' ? 'element' : 'narrator';
        const newChar = {
            id: `char-${Date.now()}`,
            name: '',
            description: '',
            category,
            makeupLevel: 'natural',
        };
        updateCharacters([...characters, newChar]);
    };

    const handleRemoveCharacter = (id) => {
        updateCharacters(characters.filter(char => char.id !== id));
    };

    const handleCloneCharacter = (char) => {
        const cloned = {
            ...char,
            id: `char-${Date.now()}`,
            name: `${char.name} (Copy)`,
            image: undefined,
            audio: undefined,
            elevenLabsVoiceId: undefined
        };
        updateCharacters([...characters, cloned]);
    };

    const handleEditPortrait = (char) => {
        setEditingCharacter(char);
    };

    const handleSavePortrait = (characterId, updates) => {
        console.log('💾 handleSavePortrait called:', {
            characterId,
            name: updates.name,
            category: updates.category,
            hasImage: !!updates.image,
            currentCharactersCount: characters.length,
            characterIds: characters.map(c => c.id)
        });
        
        // Check if this is a new character that wasn't in the list
        const existingChar = characters.find(c => c.id === characterId);
        console.log('  Existing character found:', !!existingChar);
        
        let updatedCharacters;
        if (existingChar) {
            // Update existing character
            updatedCharacters = characters.map(char => 
                char.id === characterId ? { 
                    ...char, 
                    name: updates.name,
                    description: updates.description,
                    image: updates.image,
                    category: updates.category || char.category,
                    referenceImages: updates.likenessImages,
                    costumeAndAccessoryImages: updates.costumeImages,
                    makeupLevel: updates.makeupLevel
                } : char
            );
        } else {
            // New character - add it
            const newChar = {
                id: characterId,
                name: updates.name,
                description: updates.description,
                image: updates.image,
                category: updates.category || 'character',
                referenceImages: updates.likenessImages,
                costumeAndAccessoryImages: updates.costumeImages,
                makeupLevel: updates.makeupLevel
            };
            updatedCharacters = [...characters, newChar];
            console.log('  Added new character to list');
        }
        
        console.log('  Final characters count:', updatedCharacters.length);
        
        setCharacters(updatedCharacters);
        if (onProjectUpdate) {
            console.log('  Calling onProjectUpdate with updated characters');
            onProjectUpdate({ ...initialData, characters: updatedCharacters });
        } else {
            console.warn('  ⚠️ onProjectUpdate is not defined!');
        }
        setEditingCharacter(null);
    };

    const handleAudioFileChange = async (e, id) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Credits are deducted by backend - removed upfront deduction

        setCloningStatus(prev => ({ ...prev, [id]: { status: 'cloning' } }));
        
        try {
            const base64String = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            
            const character = characters.find(c => c.id === id);
            if (!character) throw new Error("Character not found");

            const result = await cloneVoiceWithElevenLabs(character.name, base64String);
            
            // Handle both old format (string) and new format (object with credits)
            const voiceId = result?.voiceId || result;

            const newCharacters = characters.map(char => 
                char.id === id 
                ? { 
                    ...char, 
                    audio: { name: file.name, data: base64String }, 
                    elevenLabsVoiceId: voiceId 
                  } 
                : char
            );
            updateCharacters(newCharacters);
            setCloningStatus(prev => ({ ...prev, [id]: { status: 'cloned' } }));
            
            // Track credits from backend response
            if (onTrackCreditsUsed && result?.creditsUsed) {
                onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
            }

        } catch (err) {
            const errorMessage = err.message || "An unknown error occurred during cloning.";
            console.error("Voice cloning failed:", err);
            setCloningStatus(prev => ({ ...prev, [id]: { status: 'error', error: errorMessage } }));
            alert(errorMessage);
        }
    };

    const handleImageFileChange = async (e, id) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = async () => {
            let base64String = reader.result;
            
            try {
                base64String = await cropImageToAspectRatio(base64String, initialData.aspectRatio);
            } catch (e) {
                console.error("Failed to auto-crop image:", e);
            }

            handleCharacterUpdate(id, 'image', base64String);
            if (onSaveMedia) onSaveMedia([base64String]);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const validChars = characters.filter(c => c.name.trim() && c.description.trim());
        onSubmit(validChars);
    };

    const filteredCharacters = characters.filter(char => {
        if (activeTab === 'characters') return char.category === 'character' || !char.category;
        if (activeTab === 'elements') return char.category === 'element';
        if (activeTab === 'narrator') return char.category === 'narrator';
        return false;
    });

    const characterCount = characters.filter(c => c.category === 'character' || !c.category).length;
    const elementCount = characters.filter(c => c.category === 'element').length;
    const narratorCount = characters.filter(c => c.category === 'narrator').length;

    return (
        <div className="w-full h-full overflow-y-auto p-8 bg-[#0a0a0f]">
            <div className="w-full max-w-5xl mx-auto">
                <h2 className="text-3xl font-bold text-center mb-2">Flesh out Your Cast & World</h2>
                <p className="text-gray-400 text-center mb-8">
                    The AI has suggested characters and elements from your script. Review them, generate portraits, and add voice samples.
                </p>

                {error && (
                    <div className="mb-4 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-400">
                        {error}
                    </div>
                )}

                {/* Tabs */}
                <div className="flex justify-center mb-8">
                    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-1 flex space-x-1">
                        <button
                            onClick={() => setActiveTab('characters')}
                            className={`px-6 py-2 rounded-md text-sm font-semibold transition-colors ${
                                activeTab === 'characters' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-gray-700'
                            }`}
                        >
                            Characters ({characterCount})
                        </button>
                        <button
                            onClick={() => setActiveTab('elements')}
                            className={`px-6 py-2 rounded-md text-sm font-semibold transition-colors ${
                                activeTab === 'elements' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-gray-700'
                            }`}
                        >
                            Other Elements ({elementCount})
                        </button>
                        <button
                            onClick={() => setActiveTab('narrator')}
                            className={`px-6 py-2 rounded-md text-sm font-semibold transition-colors ${
                                activeTab === 'narrator' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-gray-700'
                            }`}
                        >
                            Narrator ({narratorCount})
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Character Cards */}
                    <div className="space-y-6">
                        {filteredCharacters.map((char) => {
                            if (!audioFileRefs.current[char.id]) audioFileRefs.current[char.id] = React.createRef();
                            if (!imageFileRefs.current[char.id]) imageFileRefs.current[char.id] = React.createRef();
                            
                            return (
                                <div key={char.id} className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
                                    <div className="flex gap-4">
                                        {/* Image Section */}
                                        <div className="flex-shrink-0">
                                            <div className="w-32 h-32 bg-gray-800 rounded-lg flex items-center justify-center overflow-hidden relative group">
                                                {char.image ? (
                                                    <>
                                                        <img 
                                                            src={char.image} 
                                                            alt={char.name}
                                                            className="w-full h-full object-cover"
                                                            crossOrigin="anonymous"
                                                            referrerPolicy="no-referrer"
                                                            onError={(e) => {
                                                                console.warn('Character image failed to load:', char.image?.substring(0, 60));
                                                                e.target.style.display = 'none';
                                                            }}
                                                        />
                                                        {/* Overlay with View/Delete buttons on hover */}
                                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => setViewingImage({ url: char.image, name: char.name })}
                                                                className="p-2 bg-blue-600 hover:bg-blue-700 rounded-full text-white"
                                                                title="View Image"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCharacterUpdate(char.id, 'image', null)}
                                                                className="p-2 bg-red-600 hover:bg-red-700 rounded-full text-white"
                                                                title="Delete Image"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <PhotoIcon className="w-12 h-12 text-gray-600" />
                                                )}
                                            </div>
                                            <div className="grid grid-cols-2 gap-1.5 mt-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditPortrait(char)}
                                                    className="px-2 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-md transition-colors font-semibold"
                                                >
                                                    Generate
                                                </button>
                                                {/* Upload button with dropdown */}
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleUploadClick(e, char.id)}
                                                        className="w-full px-2 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-md transition-colors font-semibold flex items-center justify-center gap-1"
                                                        data-testid={`upload-btn-${char.id}`}
                                                    >
                                                        Upload
                                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </button>
                                                    {/* Upload dropdown menu */}
                                                    {uploadMenuOpen === char.id && (
                                                        <div 
                                                            className="absolute top-full left-0 mt-1 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    imageFileRefs.current[char.id].current?.click();
                                                                    setUploadMenuOpen(null);
                                                                }}
                                                                className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-3"
                                                                data-testid={`upload-device-${char.id}`}
                                                            >
                                                                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                                </svg>
                                                                From Device
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => openAssetPicker(char.id)}
                                                                className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-3 border-t border-gray-700"
                                                                data-testid={`upload-assets-${char.id}`}
                                                            >
                                                                <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                                </svg>
                                                                From User Assets
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                <input
                                                    type="file"
                                                    ref={imageFileRefs.current[char.id]}
                                                    accept="image/*"
                                                    onChange={(e) => handleImageFileChange(e, char.id)}
                                                    className="hidden"
                                                />
                                            </div>
                                            {/* View/Delete buttons below image when image exists */}
                                            {char.image && (
                                                <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => setViewingImage({ url: char.image, name: char.name })}
                                                        className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md transition-colors flex items-center justify-center gap-1"
                                                    >
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                        View
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCharacterUpdate(char.id, 'image', null)}
                                                        className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded-md transition-colors flex items-center justify-center gap-1"
                                                    >
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Details Section */}
                                        <div className="flex-1 space-y-2">
                                            <input
                                                type="text"
                                                value={char.name}
                                                onChange={(e) => handleCharacterUpdate(char.id, 'name', e.target.value)}
                                                placeholder="Character Name"
                                                className="w-full p-2 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-base font-semibold"
                                            />
                                            <textarea
                                                value={char.description}
                                                onChange={(e) => handleCharacterUpdate(char.id, 'description', e.target.value)}
                                                placeholder="Describe appearance, personality, or details..."
                                                rows={2}
                                                className="w-full p-2 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none text-sm"
                                            />

                                            {/* Voice Section */}
                                            {activeTab === 'characters' && (
                                                <div className="flex gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => audioFileRefs.current[char.id].current?.click()}
                                                        disabled={cloningStatus[char.id]?.status === 'cloning'}
                                                        className="flex-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white text-xs rounded-md transition-colors flex items-center justify-center gap-1.5"
                                                    >
                                                        {cloningStatus[char.id]?.status === 'cloning' ? (
                                                            <><LoadingSpinner className="w-3 h-3" /> Cloning...</>
                                                        ) : cloningStatus[char.id]?.status === 'cloned' ? (
                                                            <>✓ Cloned</>
                                                        ) : (
                                                            <><MicrophoneIcon className="w-3 h-3" /> Clone</>
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="flex-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-md transition-colors"
                                                    >
                                                        Select
                                                    </button>
                                                    <input
                                                        type="file"
                                                        ref={(el) => { if (!audioFileRefs.current[char.id]) audioFileRefs.current[char.id] = { current: null }; audioFileRefs.current[char.id].current = el; }}
                                                        accept="audio/*"
                                                        onChange={(e) => handleAudioFileChange(e, char.id)}
                                                        className="hidden"
                                                    />
                                                </div>
                                            )}

                                            {/* Action Buttons */}
                                            <div className="flex justify-between items-center pt-1">
                                                <div className="flex gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCloneCharacter(char)}
                                                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-md transition-colors"
                                                    >
                                                        Clone
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-md transition-colors"
                                                    >
                                                        Select
                                                    </button>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveCharacter(char.id)}
                                                    className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 text-xs rounded-md transition-colors flex items-center gap-1.5"
                                                >
                                                    <TrashIcon className="w-3 h-3" />
                                                    Remove Character
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Add Character Button */}
                    <button
                        type="button"
                        onClick={handleAddCharacter}
                        className="w-full py-4 border-2 border-dashed border-gray-600 rounded-xl hover:border-purple-500 transition-colors flex items-center justify-center gap-2 text-gray-400 hover:text-purple-400 bg-gray-800/20"
                    >
                        <PlusIcon className="w-6 h-6" />
                        <span className="font-semibold">Add {activeTab === 'characters' ? 'Character' : activeTab === 'elements' ? 'Element' : 'Narrator'} Manually</span>
                    </button>

                    {/* Generate Ad Images Button */}
                    {initialData.script && onGenerateImages && (
                        <button
                            type="button"
                            onClick={() => onGenerateImages(characters)}
                            className="w-full mt-4 py-4 bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 hover:from-purple-700 hover:via-fuchsia-700 hover:to-pink-700 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-3 shadow-lg shadow-purple-500/20 text-base"
                            data-testid="generate-ad-images-btn"
                        >
                            <PhotoIcon className="w-5 h-5" />
                            Generate Images
                        </button>
                    )}

                    {/* Bottom Action Buttons */}
                    <div className="flex items-center gap-4 mt-8 pt-8 border-t border-gray-700">
                        <button
                            type="button"
                            onClick={onBack}
                            className="px-8 py-3 bg-gray-700 hover:bg-gray-600 text-white font-bold rounded-lg flex items-center justify-center transition-colors"
                        >
                            <ArrowLeftIcon className="w-5 h-5 mr-2" />
                            Back
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                            <MagicWandIcon className="w-5 h-5" />
                            Storyboard
                        </button>
                        {onGenerateFullVideo && (
                            <button
                                type="button"
                                onClick={() => onGenerateFullVideo(characters)}
                                className="flex-1 px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2 shadow-lg"
                            >
                                Generate Video
                            </button>
                        )}
                    </div>
                </form>

                {editingCharacter && (
                    <CharacterPortraitModal
                        character={editingCharacter}
                        projectData={initialData}
                        onClose={() => setEditingCharacter(null)}
                        onSave={handleSavePortrait}
                        onDeductCredits={onDeductCredits}
                        onTrackCreditsUsed={onTrackCreditsUsed}
                        onSaveMedia={onSaveMedia}
                    />
                )}

                {/* Image Viewing Modal */}
                {viewingImage && (
                    <div 
                        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                        onClick={() => setViewingImage(null)}
                    >
                        <div 
                            className="relative max-w-4xl max-h-[90vh] bg-gray-900 rounded-lg overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b border-gray-700">
                                <h3 className="text-lg font-semibold text-white">{viewingImage.name || 'Character Image'}</h3>
                                <button
                                    onClick={() => setViewingImage(null)}
                                    className="p-2 hover:bg-gray-700 rounded-full transition-colors"
                                >
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            {/* Image */}
                            <div className="p-4 flex items-center justify-center">
                                <img 
                                    src={viewingImage.url} 
                                    alt={viewingImage.name || 'Character'}
                                    className="max-w-full max-h-[70vh] object-contain rounded-lg"
                                />
                            </div>
                            {/* Footer with download button */}
                            <div className="flex justify-center gap-4 p-4 border-t border-gray-700">
                                <a
                                    href={viewingImage.url}
                                    download={`${viewingImage.name || 'character'}-portrait.png`}
                                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Download
                                </a>
                                <button
                                    onClick={() => setViewingImage(null)}
                                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* User Assets Picker Modal */}
                <UserAssetsPicker
                    isOpen={assetPickerOpen}
                    onClose={() => {
                        setAssetPickerOpen(false);
                        setAssetPickerTargetId(null);
                    }}
                    onSelect={handleSelectFromAssets}
                    assetType={activeTab === 'characters' ? 'actors' : activeTab === 'elements' ? 'products' : 'actors'}
                    title="Select from User Assets"
                />
            </div>
        </div>
    );
};
